import { format } from 'date-fns';
import { hi, enIN } from 'date-fns/locale';

// src/format/formatGrams.ts
var DEFAULT_LOCALE = "en-IN";
var DEFAULT_DECIMALS = 4;
var GRAM_SCALE = 6;
function render(numeric, decimals, locale) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(numeric);
}
function roundsToZero(numeric, decimals) {
  return Math.round(Math.abs(numeric) * 10 ** decimals) === 0;
}
function formatGrams(value, options = {}) {
  const { decimals = DEFAULT_DECIMALS, locale = DEFAULT_LOCALE } = options;
  const numeric = typeof value === "number" ? value : Number(value);
  if (numeric === 0 || !Number.isFinite(numeric) || !roundsToZero(numeric, decimals)) {
    return `${render(numeric, decimals, locale)} g`;
  }
  if (!roundsToZero(numeric, GRAM_SCALE)) {
    return `${render(numeric, GRAM_SCALE, locale)} g`;
  }
  const smallest = 10 ** -GRAM_SCALE;
  return numeric < 0 ? `> ${render(-smallest, GRAM_SCALE, locale)} g` : `< ${render(smallest, GRAM_SCALE, locale)} g`;
}

// src/format/formatINR.ts
var DEFAULT_LOCALE2 = "en-IN";
function toDisplayNumber(value) {
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}
function formatINR(value, options = {}) {
  const { showSymbol = true, locale = DEFAULT_LOCALE2 } = options;
  const numberFormatOptions = showSymbol ? { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 } : { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat(locale, numberFormatOptions).format(toDisplayNumber(value));
}

// src/format/quantityMath.ts
var SCALE = {
  GOLD_GRAM: 6,
  INR: 2
};
var TEN = 10n;
function scaleOf(unit) {
  return SCALE[unit];
}
function scaleFactor(unit) {
  const scale = scaleOf(unit);
  if (scale === void 0) {
    throw new TypeError(`Unknown ledger unit: "${String(unit)}"`);
  }
  return TEN ** BigInt(scale);
}
function patternFor(unit) {
  return new RegExp(`^-?\\d+(\\.\\d{1,${SCALE[unit]}})?$`);
}
function toMinorUnits(unit, value) {
  const factor = scaleFactor(unit);
  if (value === null || value === void 0) return 0n;
  const text = typeof value === "number" ? String(value) : value.trim();
  if (text === "") return 0n;
  if (!patternFor(unit).test(text)) {
    throw new TypeError(`Not a ${unit} decimal: "${text}"`);
  }
  const negative = text.startsWith("-");
  const unsigned = negative ? text.slice(1) : text;
  const dot = unsigned.indexOf(".");
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction = dot === -1 ? "" : unsigned.slice(dot + 1);
  const magnitude = BigInt(whole) * factor + BigInt(fraction.padEnd(SCALE[unit], "0"));
  return negative ? -magnitude : magnitude;
}
function fromMinorUnits(unit, value) {
  const factor = scaleFactor(unit);
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / factor;
  const fraction = (magnitude % factor).toString().padStart(SCALE[unit], "0");
  return `${negative ? "-" : ""}${whole}.${fraction}`;
}
function quantity(unit, value) {
  return { unit, value: fromMinorUnits(unit, toMinorUnits(unit, value)) };
}
function zeroQuantity(unit) {
  return { unit, value: fromMinorUnits(unit, 0n) };
}
function sameUnit(a, b) {
  if (a.unit !== b.unit) {
    throw new TypeError(`Cannot combine ${a.unit} with ${b.unit} \u2014 they are different books`);
  }
  return a.unit;
}
function addQuantity(a, b) {
  const unit = sameUnit(a, b);
  return { unit, value: fromMinorUnits(unit, toMinorUnits(unit, a.value) + toMinorUnits(unit, b.value)) };
}
function subtractQuantity(a, b) {
  const unit = sameUnit(a, b);
  return { unit, value: fromMinorUnits(unit, toMinorUnits(unit, a.value) - toMinorUnits(unit, b.value)) };
}
function sumQuantity(unit, values) {
  let total = 0n;
  for (const entry of values) {
    if (entry.unit !== unit) {
      throw new TypeError(`Cannot total a ${entry.unit} row into a ${unit} column`);
    }
    total += toMinorUnits(unit, entry.value);
  }
  return { unit, value: fromMinorUnits(unit, total) };
}
function compareQuantity(a, b) {
  const unit = sameUnit(a, b);
  const left = toMinorUnits(unit, a.value);
  const right = toMinorUnits(unit, b.value);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}
function isPositiveQuantity(value) {
  return toMinorUnits(value.unit, value.value) > 0n;
}
var BASIS_POINTS = 10000n;
function quantityRatioBasisPoints(numerator, denominator) {
  const unit = sameUnit(numerator, denominator);
  const n = toMinorUnits(unit, numerator.value);
  const d = toMinorUnits(unit, denominator.value);
  if (d <= 0n) return null;
  const negative = n < 0n;
  const scaled = (negative ? -n : n) * BASIS_POINTS;
  const rounded = (scaled * 2n + d) / (d * 2n);
  return negative ? -rounded : rounded;
}
function groupByUnit(rows, unitOf) {
  const groups = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const unit = unitOf(row);
    const bucket = groups.get(unit);
    if (bucket) bucket.push(row);
    else groups.set(unit, [row]);
  }
  return groups;
}

// src/format/formatQuantity.ts
function formatQuantity(value, unit, options = {}) {
  if (unit === "INR") return formatINR(value, options);
  if (unit === "GOLD_GRAM") return formatGrams(value, options);
  return `${String(value)} ${String(unit)}`;
}
function formatQuantityExact(value, unit, options = {}) {
  const scale = scaleOf(unit);
  return formatQuantity(value, unit, scale === void 0 ? options : { ...options, decimals: scale });
}

// src/format/gramsMath.ts
var GOLD_GRAM = "GOLD_GRAM";
function toMicrograms(value) {
  return toMinorUnits(GOLD_GRAM, value);
}
function fromMicrograms(value) {
  return fromMinorUnits(GOLD_GRAM, value);
}
function addGrams(a, b) {
  return fromMicrograms(toMicrograms(a) + toMicrograms(b));
}
function subtractGrams(a, b) {
  return fromMicrograms(toMicrograms(a) - toMicrograms(b));
}
function sumGrams(values) {
  let total = 0n;
  for (const value of values) {
    total += toMicrograms(value);
  }
  return fromMicrograms(total);
}
function compareGrams(a, b) {
  const left = toMicrograms(a);
  const right = toMicrograms(b);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}
function ratioBasisPoints(numerator, denominator) {
  return quantityRatioBasisPoints(
    { unit: GOLD_GRAM, value: fromMicrograms(toMicrograms(numerator)) },
    { unit: GOLD_GRAM, value: fromMicrograms(toMicrograms(denominator)) }
  );
}

// src/format/paisaMath.ts
var FRACTION_DIGITS = 2;
var SCALE_FACTOR = 100n;
var RUPEE_DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;
function toPaisa(value) {
  if (value === null || value === void 0) return null;
  const cleaned = (typeof value === "number" ? String(value) : value).replace(/[\s,]/g, "");
  if (!cleaned || !RUPEE_DECIMAL_PATTERN.test(cleaned)) return null;
  const negative = cleaned.startsWith("-");
  const unsigned = negative ? cleaned.slice(1) : cleaned;
  const dot = unsigned.indexOf(".");
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction = dot === -1 ? "" : unsigned.slice(dot + 1);
  const magnitude = BigInt(whole) * SCALE_FACTOR + BigInt(fraction.padEnd(FRACTION_DIGITS, "0").slice(0, FRACTION_DIGITS));
  return negative ? -magnitude : magnitude;
}
function fromPaisa(value) {
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / SCALE_FACTOR;
  const fraction = (magnitude % SCALE_FACTOR).toString().padStart(FRACTION_DIGITS, "0");
  return `${negative ? "-" : ""}${whole}.${fraction}`;
}
function addRupees(a, b) {
  const left = toPaisa(a);
  const right = toPaisa(b);
  if (left === null || right === null) return null;
  return fromPaisa(left + right);
}
function subtractRupees(a, b) {
  const left = toPaisa(a);
  const right = toPaisa(b);
  if (left === null || right === null) return null;
  return fromPaisa(left - right);
}
function sumRupees(values) {
  let total = 0n;
  let counted = 0;
  let skipped = 0;
  for (const value of values) {
    const paisa = toPaisa(value);
    if (paisa === null) {
      skipped += 1;
      continue;
    }
    total += paisa;
    counted += 1;
  }
  return { total: fromPaisa(total), counted, skipped };
}
function multiplyRupees(value, factor) {
  if (!Number.isSafeInteger(factor)) return null;
  const paisa = toPaisa(value);
  if (paisa === null) return null;
  return fromPaisa(paisa * BigInt(factor));
}
function compareRupees(a, b) {
  const left = toPaisa(a);
  const right = toPaisa(b);
  if (left === null || right === null) return null;
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}
function isPositiveRupees(value) {
  const paisa = toPaisa(value);
  return paisa !== null && paisa > 0n;
}

// src/format/formatPercent.ts
var DEFAULT_DECIMALS2 = 2;
function formatPercent(value, decimals = DEFAULT_DECIMALS2) {
  const rounded = Number(value.toFixed(decimals));
  const normalized = rounded === 0 ? 0 : rounded;
  return `${normalized.toFixed(decimals)}%`;
}

// src/format/formatCompactNumber.ts
var INDIC_LOCALES = /* @__PURE__ */ new Set(["en-IN", "hi-IN", "en", "hi"]);
var CRORE = 1e7;
var LAKH = 1e5;
var WESTERN_BILLION = 1e9;
var WESTERN_MILLION = 1e6;
var THOUSAND = 1e3;
function formatCompactNumber(value, options = {}) {
  const { locale = "en-IN" } = options;
  const sign = value < 0 ? "-" : "";
  const magnitude = Math.abs(value);
  if (INDIC_LOCALES.has(locale)) {
    if (magnitude >= CRORE) return `${sign}${trimTrailingZero((magnitude / CRORE).toFixed(1))}Cr`;
    if (magnitude >= LAKH) return `${sign}${trimTrailingZero((magnitude / LAKH).toFixed(1))}L`;
    if (magnitude >= THOUSAND) return `${sign}${trimTrailingZero((magnitude / THOUSAND).toFixed(1))}K`;
    return `${sign}${magnitude}`;
  }
  if (magnitude >= WESTERN_BILLION) return `${sign}${trimTrailingZero((magnitude / WESTERN_BILLION).toFixed(1))}B`;
  if (magnitude >= WESTERN_MILLION) return `${sign}${trimTrailingZero((magnitude / WESTERN_MILLION).toFixed(1))}M`;
  if (magnitude >= THOUSAND) return `${sign}${trimTrailingZero((magnitude / THOUSAND).toFixed(1))}K`;
  return `${sign}${magnitude}`;
}
function trimTrailingZero(formatted) {
  return formatted.endsWith(".0") ? formatted.slice(0, -2) : formatted;
}

// src/validation/isValidPhone.ts
var INDIAN_MOBILE_REGEX = /^(?:\+91)?[6-9]\d{9}$/;
function isValidPhone(value) {
  if (typeof value !== "string") return false;
  return INDIAN_MOBILE_REGEX.test(value.trim());
}

// src/validation/isValidPan.ts
var PAN_REGEX = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;
function isValidPan(value) {
  if (typeof value !== "string") return false;
  return PAN_REGEX.test(value.trim());
}

// src/validation/isValidAadhaarMasked.ts
var MASKED_AADHAAR_REGEX = /^XXXX XXXX \d{4}$/;
function isValidAadhaarMasked(value) {
  if (typeof value !== "string") return false;
  return MASKED_AADHAAR_REGEX.test(value.trim());
}

// src/validation/isValidOtp.ts
var DEFAULT_OTP_LENGTH = 6;
function isValidOtp(value, length = DEFAULT_OTP_LENGTH) {
  if (typeof value !== "string") return false;
  if (!Number.isInteger(length) || length <= 0) return false;
  const pattern = new RegExp(`^\\d{${length}}$`);
  return pattern.test(value.trim());
}

// src/date/formatRelativeDate.ts
var DEFAULT_LOCALE3 = "en-IN";
var DIVISIONS = [
  { amount: 60, unit: "seconds" },
  { amount: 60, unit: "minutes" },
  { amount: 24, unit: "hours" },
  { amount: 7, unit: "days" },
  { amount: 4.34524, unit: "weeks" },
  { amount: 12, unit: "months" },
  { amount: Number.POSITIVE_INFINITY, unit: "years" }
];
function formatRelativeDate(date) {
  const target = typeof date === "string" ? new Date(date) : date;
  const formatter = new Intl.RelativeTimeFormat(DEFAULT_LOCALE3, { numeric: "auto" });
  let duration = (target.getTime() - Date.now()) / 1e3;
  if (Number.isNaN(duration)) return "Invalid date";
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return formatter.format(Math.round(duration), "years");
}
var DEFAULT_PATTERN = "dd MMM yyyy";
var DEFAULT_LOCALE4 = "en-IN";
var LOCALE_MAP = {
  "en-IN": enIN,
  en: enIN,
  "hi-IN": hi,
  hi
};
function formatDate(date, pattern = DEFAULT_PATTERN, locale = DEFAULT_LOCALE4) {
  const target = typeof date === "string" ? new Date(date) : date;
  const resolvedLocale = LOCALE_MAP[locale] ?? enIN;
  return format(target, pattern, { locale: resolvedLocale });
}

// src/date/holidays.ts
var BANK_HOLIDAYS = /* @__PURE__ */ new Set([
  // 2026
  "2026-01-26",
  // Republic Day
  "2026-03-04",
  // Holi
  "2026-04-03",
  // Good Friday
  "2026-08-15",
  // Independence Day
  "2026-10-02",
  // Gandhi Jayanti
  "2026-10-20",
  // Dussehra
  "2026-11-08",
  // Diwali
  "2026-12-25"
  // Christmas
]);
function isBankHoliday(date) {
  return BANK_HOLIDAYS.has(toIsoDate(date));
}
function toIsoDate(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// src/date/isBusinessDay.ts
var SUNDAY = 0;
var SATURDAY = 6;
function isBusinessDay(date) {
  const day = date.getDay();
  if (day === SUNDAY || day === SATURDAY) return false;
  return !isBankHoliday(date);
}

// src/date/addBusinessDays.ts
function addBusinessDays(date, days) {
  const result = new Date(date.getTime());
  const direction = days < 0 ? -1 : 1;
  let remaining = Math.abs(Math.trunc(days));
  while (remaining > 0) {
    result.setDate(result.getDate() + direction);
    if (isBusinessDay(result)) {
      remaining -= 1;
    }
  }
  return result;
}

// src/id/generateCorrelationId.ts
function generateCorrelationId() {
  const cryptoObj = getCrypto();
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    return uuidFromRandomBytes(cryptoObj);
  }
  return uuidFromMathRandom();
}
function getCrypto() {
  if (typeof globalThis !== "undefined" && "crypto" in globalThis) {
    return globalThis.crypto;
  }
  return void 0;
}
function uuidFromRandomBytes(cryptoObj) {
  const bytes = new Uint8Array(16);
  cryptoObj.getRandomValues(bytes);
  const versionByte = bytes[6];
  const variantByte = bytes[8];
  if (versionByte === void 0 || variantByte === void 0) return uuidFromMathRandom();
  bytes[6] = versionByte & 15 | 64;
  bytes[8] = variantByte & 63 | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join("")
  ].join("-");
}
function uuidFromMathRandom() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === "x" ? random : random & 3 | 8;
    return value.toString(16);
  });
}

// src/retry/computeBackoffDelay.ts
var DEFAULT_BASE_MS = 250;
var DEFAULT_MAX_MS = 3e4;
function computeBackoffDelay(attempt, options = {}) {
  const { baseMs = DEFAULT_BASE_MS, maxMs = DEFAULT_MAX_MS } = options;
  const safeAttempt = Math.max(0, Math.trunc(attempt));
  const exponentialDelay = baseMs * 2 ** safeAttempt;
  const cappedDelay = Math.min(exponentialDelay, maxMs);
  return Math.floor(Math.random() * cappedDelay);
}

export { addBusinessDays, addGrams, addQuantity, addRupees, compareGrams, compareQuantity, compareRupees, computeBackoffDelay, formatCompactNumber, formatDate, formatGrams, formatINR, formatPercent, formatQuantity, formatQuantityExact, formatRelativeDate, fromMicrograms, fromMinorUnits, fromPaisa, generateCorrelationId, groupByUnit, isBusinessDay, isPositiveQuantity, isPositiveRupees, isValidAadhaarMasked, isValidOtp, isValidPan, isValidPhone, multiplyRupees, quantity, quantityRatioBasisPoints, ratioBasisPoints, scaleOf, subtractGrams, subtractQuantity, subtractRupees, sumGrams, sumQuantity, sumRupees, toMicrograms, toMinorUnits, toPaisa, zeroQuantity };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map